/**
 * Pricebook List View Container
 *
 * First row: Standard pricebook (links to standard detail). Remaining rows: one per firm (Account)
 * with Firm name, Managing partner, Uses default vs Custom; click navigates to correct pricebook detail.
 */

import React, { useState, useMemo } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { usePricebooks } from '../../services/queries/pricebookQueries';
import { useAccounts } from '../../services/queries/accountQueries';
import { PricebookModal } from '../modals/PricebookModal';
import { useToast } from '../../contexts/ToastContext';
import { persistedContactRecords } from '../../services/mockData/contacts';
import type { Pricebook } from '../../types/index';

interface PricebookListViewProps {
  onNavigateToPricebook: (pricebookId: string) => void;
  onCreatePricebook?: () => void;
}

type ListRow =
  | { type: 'standard'; label: string; pricebookId: string }
  | { type: 'firm'; firmName: string; partnerName: string; useDefault: boolean; pricebookId: string; accountId: string }
  | { type: 'custom'; label: string; pricebookId: string };

export const PricebookListView: React.FC<PricebookListViewProps> = ({
  onNavigateToPricebook,
  onCreatePricebook
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isPricebookModalOpen, setIsPricebookModalOpen] = useState(false);
  const [selectedPricebook, setSelectedPricebook] = useState<Pricebook | null>(null);
  const { data: pricebooks, isLoading: pricebooksLoading } = usePricebooks();
  const { data: accounts, isLoading: accountsLoading } = useAccounts();

  const rows = useMemo((): ListRow[] => {
    if (!pricebooks || !accounts) return [];
    let standard = pricebooks.find(pb => pb.isStandard);
    if (!standard && pricebooks.length > 0) {
      standard = pricebooks[0];
    }
    if (!standard) return [];
    const contacts = persistedContactRecords.get();
    const getPartnerName = (partnerId: string) => contacts.find((c: { id: string; name?: string }) => c.id === partnerId)?.name ?? '—';
    const result: ListRow[] = [
      { type: 'standard', label: 'Standard Pricebook', pricebookId: standard.id }
    ];
    const accountMap = new Map(accounts.map(a => [a.id, a]));
    const firmPricebooks = pricebooks.filter(pb => pb.accountId && pb.id !== standard!.id);
    firmPricebooks.forEach(pb => {
      const accountId = pb.accountId!;
      const firmName = accountMap.get(accountId)?.name ?? accountId;
      const partnerName = pb.partnerId ? getPartnerName(pb.partnerId) : '—';
      const useDefault = pb.useStandardPricebook ?? false;
      result.push({
        type: 'firm',
        firmName,
        partnerName,
        useDefault,
        pricebookId: pb.id,
        accountId
      });
    });
    const otherPricebooks = pricebooks.filter(pb => pb.id !== standard!.id && !pb.accountId);
    otherPricebooks.forEach(pb => {
      result.push({
        type: 'custom',
        label: pb.name || 'Pricebook',
        pricebookId: pb.id
      });
    });
    return result;
  }, [pricebooks, accounts]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(row => {
      if (row.type === 'standard' || row.type === 'custom') return row.label.toLowerCase().includes(q);
      return row.firmName.toLowerCase().includes(q) || row.partnerName.toLowerCase().includes(q);
    });
  }, [rows, searchQuery]);

  const handleRowClick = (row: ListRow) => {
    onNavigateToPricebook(row.pricebookId);
  };

  const isLoading = pricebooksLoading || accountsLoading;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f172a] p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Pricebooks</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage pricing catalogs and product pricing
            </p>
          </div>
          <button
            onClick={() => {
              if (onCreatePricebook) {
                onCreatePricebook();
              } else {
                setSelectedPricebook(null);
                setIsPricebookModalOpen(true);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Pricebook
          </button>
        </div>
      </div>

      <div className="mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search pricebooks..."
            className="w-full pl-10 pr-10 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading pricebooks...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Managing Partner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Pricing
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#1e293b] divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRows.map((row) => (
                <tr
                  key={row.type === 'standard' || row.type === 'custom' ? row.pricebookId : `${row.accountId}-${row.pricebookId}`}
                  onClick={() => handleRowClick(row)}
                  className="hover:bg-gray-50 dark:hover:bg-[#334155] cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {row.type === 'firm' ? row.firmName : row.label}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {row.type === 'firm' ? row.partnerName : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.type === 'standard' ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Standard
                      </span>
                    ) : row.type === 'custom' ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        Custom
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        row.useDefault
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                      }`}>
                        {row.useDefault ? 'Uses default' : 'Custom pricebook'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRows.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No pricebooks found</p>
            </div>
          )}
        </div>
      )}

      <PricebookModal
        isOpen={isPricebookModalOpen}
        onClose={() => {
          setIsPricebookModalOpen(false);
          setSelectedPricebook(null);
        }}
        pricebook={selectedPricebook}
      />
    </div>
  );
};
